import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft, ShoppingBag, Loader2, Truck, RefreshCcw, CreditCard, Check } from 'lucide-react';
import { useCartStore } from '@/lib/store';
import { toast } from 'sonner';

const COLOR_MAP = {
  nero: '#1c1917',
  bianco: '#fafaf9', avorio: '#fafaf9',
  blu: '#1e3a5f',
  champagne: '#d4af37', oro: '#d4af37',
  sabbia: '#c2b280', naturale: '#c2b280',
  corallo: '#ff6f61', rosa: '#ff6f61',
  verde: '#556b2f',
  grigio: '#9ca3af', argento: '#9ca3af',
  tartaruga: '#8b4513',
  azzurro: '#87ceeb',
};

function getColorHex(colorName) {
  const lower = colorName.toLowerCase();
  for (const [key, hex] of Object.entries(COLOR_MAP)) {
    if (lower.includes(key)) return hex;
  }
  return '#9ca3af';
}

// All products data (same as Shop.jsx)
const allProducts = [
  // Donna
  {
    id: 'donna-1',
    name: 'La Villa Signature One-Piece',
    price: 189,
    description: 'Un intero che racconta eleganza senza tempo. Taglio sartoriale, tessuto italiano stretch che accarezza ogni curva. Perfetto dal bordo piscina all\'aperitivo.',
    image: 'https://images.unsplash.com/photo-1616594529167-6859b22fdcd1?w=800&q=85',
    images: [
      'https://images.unsplash.com/photo-1616594529167-6859b22fdcd1?w=800&q=85',
      'https://images.unsplash.com/photo-1694120105801-b51db377f758?w=800&q=85',
    ],
    colors: ['Nero', 'Avorio', 'Blu Notte'],
    sizes: ['XS', 'S', 'M', 'L', 'XL'],
    category: 'Donna',
    materials: '80% Poliammide, 20% Elastan. Tessuto italiano premium con protezione UV.',
    care: 'Lavaggio a mano in acqua fredda. Non candeggiare. Asciugare all\'ombra.',
  },
  {
    id: 'donna-2',
    name: 'La Villa Pampelonne Bikini',
    price: 159,
    description: 'Ispirato alle spiagge più esclusive della Costa Azzurra. Due pezzi dal fit impeccabile che valorizza ogni silhouette.',
    image: 'https://images.unsplash.com/photo-1674954273872-22e372b2bc43?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1674954273872-22e372b2bc43?w=800&q=85'],
    colors: ['Champagne', 'Corallo', 'Bianco'],
    sizes: ['XS', 'S', 'M', 'L'],
    category: 'Donna',
    materials: '78% Poliammide, 22% Elastan. Fodera interna per comfort ottimale.',
    care: 'Lavaggio a mano. Non strizzare. Asciugare in piano.',
  },
  {
    id: 'donna-3',
    name: 'La Villa Riviera Cover-Up',
    price: 129,
    description: 'Dal bordo piscina al tramonto. Un copricostume in lino che veste ogni momento con naturale eleganza.',
    image: 'https://images.unsplash.com/photo-1772305436752-4f5f7eaf279d?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1772305436752-4f5f7eaf279d?w=800&q=85'],
    colors: ['Bianco', 'Sabbia'],
    sizes: ['S/M', 'M/L'],
    category: 'Donna',
    materials: '100% Lino italiano. Tessuto leggero e traspirante.',
    care: 'Lavaggio delicato a 30°. Stirare a temperatura media.',
  },
  {
    id: 'donna-4',
    name: 'La Villa Beach Dress',
    price: 219,
    description: 'Lino pregiato che danza con la brezza estiva. Per chi ama il lusso discreto e la semplicità raffinata.',
    image: 'https://images.unsplash.com/photo-1694120105801-b51db377f758?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1694120105801-b51db377f758?w=800&q=85'],
    colors: ['Avorio', 'Blu Cielo'],
    sizes: ['XS', 'S', 'M', 'L'],
    category: 'Donna',
    materials: '100% Lino europeo. Tinto in filo per colori duraturi.',
    care: 'Lavaggio a 30°. Stirare leggermente umido.',
  },
  {
    id: 'donna-5',
    name: 'La Villa Linen Set',
    price: 279,
    description: 'Completo due pezzi in lino italiano. Eleganza effortless dal giorno alla sera, dalla piscina al ristorante.',
    image: 'https://images.unsplash.com/photo-1727896915523-5539f7d5f672?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1727896915523-5539f7d5f672?w=800&q=85'],
    colors: ['Sabbia', 'Nero'],
    sizes: ['XS', 'S', 'M', 'L'],
    category: 'Donna',
    materials: '100% Lino italiano premium. Bottoni in madreperla naturale.',
    care: 'Lavaggio delicato. Non asciugare in asciugatrice.',
  },
  // Uomo
  {
    id: 'uomo-1',
    name: 'La Villa Riviera Swim Short',
    price: 119,
    description: 'Costume da bagno in tessuto quick-dry con dettagli sartoriali. Il gentleman del poolside merita il meglio.',
    image: 'https://images.unsplash.com/photo-1728048756954-be23bd048b56?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1728048756954-be23bd048b56?w=800&q=85'],
    colors: ['Blu Navy', 'Verde Oliva', 'Nero'],
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    category: 'Uomo',
    materials: '100% Poliestere riciclato. Quick-dry technology.',
    care: 'Lavaggio a 30°. Non stirare.',
  },
  {
    id: 'uomo-2',
    name: 'La Villa Summer Shirt',
    price: 149,
    description: 'Camicia in cotone egiziano. Fresca, impeccabile, per ogni aperitivo al tramonto che si rispetti.',
    image: 'https://images.unsplash.com/photo-1681250919919-b9bebdee4a75?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1681250919919-b9bebdee4a75?w=800&q=85'],
    colors: ['Bianco', 'Azzurro', 'Rosa Cipria'],
    sizes: ['S', 'M', 'L', 'XL'],
    category: 'Uomo',
    materials: '100% Cotone egiziano. Tessuto popeline 120/2.',
    care: 'Lavaggio a 40°. Stirare a temperatura media-alta.',
  },
  {
    id: 'uomo-3',
    name: 'La Villa Linen Shirt',
    price: 169,
    description: 'Lino italiano al suo meglio. Eleganza naturale che respira con te durante le giornate più calde.',
    image: 'https://images.unsplash.com/photo-1771924492292-450192eb60f1?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1771924492292-450192eb60f1?w=800&q=85'],
    colors: ['Sabbia', 'Bianco', 'Blu'],
    sizes: ['S', 'M', 'L', 'XL'],
    category: 'Uomo',
    materials: '100% Lino italiano. Bottoni in madreperla.',
    care: 'Lavaggio a 30°. Stirare leggermente umido.',
  },
  {
    id: 'uomo-4',
    name: 'La Villa Resort Polo',
    price: 129,
    description: 'Polo in piqué premium. Per chi non scende a compromessi, nemmeno in vacanza.',
    image: 'https://images.unsplash.com/photo-1771924492224-e0b63b8d2c54?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1771924492224-e0b63b8d2c54?w=800&q=85'],
    colors: ['Nero', 'Bianco', 'Champagne'],
    sizes: ['S', 'M', 'L', 'XL'],
    category: 'Uomo',
    materials: '100% Cotone piqué. Logo ricamato.',
    care: 'Lavaggio a 40°. Non candeggiare.',
  },
  {
    id: 'uomo-5',
    name: 'La Villa Pool Short',
    price: 99,
    description: 'Bermuda in terry cotton. Comfort assoluto dal lettino al bar, senza perdere un grammo di stile.',
    image: 'https://images.unsplash.com/photo-1641175994857-1d9bd5c6f678?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1641175994857-1d9bd5c6f678?w=800&q=85'],
    colors: ['Grigio', 'Navy', 'Bianco'],
    sizes: ['S', 'M', 'L', 'XL'],
    category: 'Uomo',
    materials: '100% Cotone terry. Morbido e assorbente.',
    care: 'Lavaggio a 40°. Asciugatura in asciugatrice consentita.',
  },
  // Accessori
  {
    id: 'acc-1',
    name: 'La Villa Sun Towel',
    price: 89,
    description: 'Telo mare in cotone jacquard. Morbidezza e stile firmati La Villa. Generose dimensioni per il massimo comfort.',
    image: 'https://images.unsplash.com/photo-1719386215224-7cd18ee967bf?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1719386215224-7cd18ee967bf?w=800&q=85'],
    colors: ['Avorio/Oro', 'Blu/Bianco'],
    sizes: ['Unica'],
    category: 'Accessori',
    materials: '100% Cotone jacquard. Dimensioni 180x100cm.',
    care: 'Lavaggio a 40°. Non candeggiare.',
  },
  {
    id: 'acc-2',
    name: 'La Villa Club Cap',
    price: 59,
    description: 'Cappellino in cotone organico. Il tuo pass per il club, in versione indossabile.',
    image: 'https://images.unsplash.com/photo-1620075223759-f0181cbac99d?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1620075223759-f0181cbac99d?w=800&q=85'],
    colors: ['Nero', 'Bianco', 'Navy'],
    sizes: ['Unica'],
    category: 'Accessori',
    materials: '100% Cotone organico. Logo ricamato.',
    care: 'Lavaggio a mano. Asciugare all\'aria.',
  },
  {
    id: 'acc-3',
    name: 'La Villa Straw Hat',
    price: 79,
    description: 'Cappello in paglia naturale intrecciato a mano. Ombra elegante per le giornate più luminose.',
    image: 'https://images.unsplash.com/photo-1766228477877-012403fad844?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1766228477877-012403fad844?w=800&q=85'],
    colors: ['Naturale', 'Nero'],
    sizes: ['S/M', 'M/L'],
    category: 'Accessori',
    materials: 'Paglia naturale intrecciata a mano. Nastro in grosgrain.',
    care: 'Non lavare. Conservare in luogo asciutto.',
  },
  {
    id: 'acc-4',
    name: 'La Villa Thermal Bottle',
    price: 49,
    description: 'Borraccia termica in acciaio. Idratazione con stile, sempre con te. Mantiene le bevande fresche 24 ore.',
    image: 'https://images.unsplash.com/photo-1706638312007-5b39c6478ea2?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1706638312007-5b39c6478ea2?w=800&q=85'],
    colors: ['Nero Opaco', 'Oro Rosa', 'Argento'],
    sizes: ['500ml'],
    category: 'Accessori',
    materials: 'Acciaio inossidabile 18/8. Doppia parete isolante.',
    care: 'Lavaggio a mano. Non in lavastoviglie.',
  },
  {
    id: 'acc-5',
    name: 'La Villa Beach Bag',
    price: 149,
    description: 'Borsa mare in rafia intrecciata. Capiente, elegante, indispensabile per ogni giornata in piscina.',
    image: 'https://images.unsplash.com/photo-1687977468316-903249c33a98?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1687977468316-903249c33a98?w=800&q=85'],
    colors: ['Naturale', 'Nero'],
    sizes: ['Unica'],
    category: 'Accessori',
    materials: 'Rafia naturale. Fodera interna in cotone.',
    care: 'Non lavare. Conservare in sacchetto di cotone.',
  },
  {
    id: 'acc-6',
    name: 'La Villa Sunglasses',
    price: 199,
    description: 'Occhiali da sole con montatura in acetato italiano. Protezione UV400. Stile senza compromessi.',
    image: 'https://images.unsplash.com/photo-1766484695068-ace3ec90e33a?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1766484695068-ace3ec90e33a?w=800&q=85'],
    colors: ['Tartaruga', 'Nero', 'Champagne'],
    sizes: ['Unica'],
    category: 'Accessori',
    materials: 'Acetato italiano. Lenti polarizzate UV400.',
    care: 'Pulire con panno in microfibra. Custodire nell\'astuccio.',
  },
  {
    id: 'acc-7',
    name: 'La Villa Tote',
    price: 129,
    description: 'Tote bag in canvas premium. Per chi porta La Villa ovunque, con stile.',
    image: 'https://images.unsplash.com/photo-1616594529167-6859b22fdcd1?w=800&q=85',
    images: ['https://images.unsplash.com/photo-1616594529167-6859b22fdcd1?w=800&q=85'],
    colors: ['Nero', 'Sabbia'],
    sizes: ['Unica'],
    category: 'Accessori',
    materials: 'Canvas 100% cotone 18oz. Manici in pelle.',
    care: 'Lavaggio a mano o a secco.',
  },
];

// Related products function
const getRelatedProducts = (currentProduct, count = 4) => {
  return allProducts
    .filter(p => p.category === currentProduct.category && p.id !== currentProduct.id)
    .slice(0, count);
};

export default function ProductDetail() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  
  const addItem = useCartStore((state) => state.addItem);

  useEffect(() => {
    // Find product from local data
    const foundProduct = allProducts.find(p => p.id === productId);
    if (foundProduct) {
      setProduct(foundProduct);
      setSelectedSize(foundProduct.sizes[0]);
      setSelectedColor(foundProduct.colors[0]);
    }
    setLoading(false);
  }, [productId]);

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast.error('Seleziona una taglia');
      return;
    }
    if (!selectedColor) {
      toast.error('Seleziona un colore');
      return;
    }
    addItem(product, quantity, selectedSize, selectedColor);
    toast.success('Aggiunto al carrello');
  };

  if (loading) {
    return (
      <div className="bg-white pt-32 min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-stone-400" size={40} />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-white pt-32 min-h-screen">
        <div className="max-w-[1800px] mx-auto px-6 py-24 text-center">
          <p className="font-body text-stone-500 mb-6">Prodotto non trovato</p>
          <Button asChild variant="outline" className="rounded-none">
            <Link to="/shop">
              <ArrowLeft className="mr-2" size={16} />
              Torna allo Shop
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const relatedProducts = getRelatedProducts(product);

  return (
    <div className="bg-white pt-24 min-h-screen" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <section className="pb-8 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <Link
            to="/shop"
            className="font-body text-xs uppercase tracking-widest text-stone-500 hover:text-night transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft size={14} />
            Torna alla Collection
          </Link>
        </div>
      </section>

      {/* Product */}
      <section className="pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Images */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4"
            >
              {/* Main Image */}
              <div className="aspect-[3/4] bg-stone-100 overflow-hidden">
                <motion.img
                  key={selectedImage}
                  src={product.images?.[selectedImage] || product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              
              {/* Thumbnails */}
              {product.images && product.images.length > 1 && (
                <div className="flex gap-4">
                  {product.images.map((img, idx) => (
                    <button
                      key={img}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-20 h-24 overflow-hidden ${
                        selectedImage === idx ? 'ring-2 ring-night' : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:py-8"
            >
              <p className="font-body text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">
                {product.category}
              </p>
              <h1 className="font-heading text-3xl md:text-4xl text-night tracking-tight mb-4" data-testid="product-title">
                {product.name}
              </h1>
              <div className="flex items-center gap-3 mb-8">
                <span className="font-body text-2xl text-stone-400 blur-[4px] select-none">€{product.price}</span>
                <span className="font-body text-sm uppercase tracking-wider text-champagne bg-champagne/10 px-3 py-1">TBA</span>
              </div>
              
              <p className="font-body text-stone-600 leading-relaxed mb-10">
                {product.description}
              </p>

              {/* Color Selector */}
              <div className="mb-8">
                <p className="font-body text-xs uppercase tracking-widest text-stone-600 mb-4">
                  Colore: <span className="text-night">{selectedColor}</span>
                </p>
                <div className="flex gap-3">
                  {product.colors.map(color => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${
                        selectedColor === color ? 'border-night scale-110' : 'border-stone-200'
                      }`}
                      style={{
                        backgroundColor: getColorHex(color)
                      }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              {/* Size Selector */}
              <div className="mb-8">
                <p className="font-body text-xs uppercase tracking-widest text-stone-600 mb-4">
                  Taglia
                </p>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map(size => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`min-w-[50px] px-4 py-3 text-sm font-body uppercase tracking-wider transition-all ${
                        selectedSize === size 
                          ? 'bg-night text-white' 
                          : 'bg-white border border-stone-200 text-stone-600 hover:border-night'
                      }`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-10">
                <p className="font-body text-xs uppercase tracking-widest text-stone-600 mb-4">
                  Quantità
                </p>
                <div className="flex items-center">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors"
                    data-testid="decrease-quantity"
                  >
                    -
                  </button>
                  <span className="w-16 h-12 border-t border-b border-stone-200 flex items-center justify-center font-body">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors"
                    data-testid="increase-quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart */}
              <Button
                disabled={true}
                className="w-full bg-stone-400 text-white rounded-none py-6 text-sm uppercase tracking-widest mb-8 cursor-not-allowed"
                data-testid="add-to-cart-button"
              >
                <ShoppingBag className="mr-2" size={18} />
                Presto Disponibile
              </Button>

              {/* Info Accordion */}
              <Accordion type="single" collapsible className="border-t border-stone-200">
                <AccordionItem value="description" className="border-b border-stone-200">
                  <AccordionTrigger className="font-body text-sm uppercase tracking-widest text-night py-5 hover:no-underline">
                    Descrizione
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-stone-600 pb-5">
                    {product.description}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="materials" className="border-b border-stone-200">
                  <AccordionTrigger className="font-body text-sm uppercase tracking-widest text-night py-5 hover:no-underline">
                    Materiali
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-stone-600 pb-5">
                    {product.materials}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="care" className="border-b border-stone-200">
                  <AccordionTrigger className="font-body text-sm uppercase tracking-widest text-night py-5 hover:no-underline">
                    Cura del Prodotto
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-stone-600 pb-5">
                    {product.care}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="shipping" className="border-b border-stone-200">
                  <AccordionTrigger className="font-body text-sm uppercase tracking-widest text-night py-5 hover:no-underline">
                    Spedizione & Resi
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-stone-600 pb-5 space-y-4">
                    <div className="flex items-start gap-3">
                      <Truck size={18} className="text-stone-400 mt-0.5" />
                      <div>
                        <p className="font-semibold text-night">Spedizione Gratuita</p>
                        <p>Per ordini superiori a €150. Consegna in 3-5 giorni lavorativi.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <RefreshCcw size={18} className="text-stone-400 mt-0.5" />
                      <div>
                        <p className="font-semibold text-night">Reso Gratuito</p>
                        <p>Entro 30 giorni dalla consegna. Articoli non utilizzati con etichette.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CreditCard size={18} className="text-stone-400 mt-0.5" />
                      <div>
                        <p className="font-semibold text-night">Pagamenti Sicuri</p>
                        <p>Carte di credito, PayPal, Apple Pay. Transazioni crittografate.</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-stone-50">
          <div className="max-w-[1800px] mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-night tracking-tight mb-12 text-center">
              Potrebbe Piacerti
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.map((relProduct) => (
                <Link
                  key={relProduct.id}
                  to={`/shop/${relProduct.id}`}
                  className="group"
                >
                  <div className="aspect-[3/4] overflow-hidden bg-white mb-4">
                    <img
                      src={relProduct.image}
                      alt={relProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="font-body text-sm text-night group-hover:text-stone-600 transition-colors">
                    {relProduct.name}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="font-body text-sm text-stone-400 blur-[3px] select-none">€{relProduct.price}</span>
                    <span className="font-body text-[10px] uppercase tracking-wider text-champagne">TBA</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom padding for mobile CTA */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
